import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../ui/LoadingSpinner';

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const { values, errors, handleChange, handleSubmit, isValid, isSubmitting } = useFormValidation(
    {
      password: '',
      confirmPassword: '',
    },
    {
      password: (value) => {
        if (!value) return t('auth.validation.passwordRequired');
        if (value.length < 6) return t('auth.validation.passwordMinLength');
        return '';
      },
      confirmPassword: (value, allValues) => {
        if (!value) return t('auth.validation.confirmPasswordRequired');
        if (value !== allValues.password) return t('auth.validation.passwordsDoNotMatch');
        return '';
      },
    },
    async (formValues) => {
      try {
        const { error: updateError } = await supabase.auth.updateUser({
          password: formValues.password
        });

        if (updateError) throw updateError;

        setSuccess(true);
        setTimeout(() => {
          navigate('/', { 
            replace: true,
            state: { message: t('auth.passwordUpdatedSuccess') }
          });
        }, 2000);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.resetPasswordFailed'));
        throw err;
      }
    }
  );

  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsCheckingSession(true);
        
        // Check if we have URL fragments (for web)
        const hash = window.location.hash;
        if (hash && hash.includes('access_token')) {
          const params = new URLSearchParams(hash.substring(1));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          
          if (accessToken && refreshToken) {
            const { data: { session }, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (sessionError) throw sessionError;
            if (session) {
              setIsValidSession(true);
              setIsCheckingSession(false);
              return;
            }
          }
        }

        // Check if we have search params (for mobile deep links)
        const searchParams = new URLSearchParams(location.search);
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        
        if (accessToken && refreshToken) {
          const { data: { session }, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (sessionError) throw sessionError;
          if (session) {
            setIsValidSession(true);
            setIsCheckingSession(false);
            return;
          }
        }

        // Check current session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setIsValidSession(true);
          setIsCheckingSession(false);
          return;
        }

        // No valid session found
        throw new Error('No valid session found');
        
      } catch (err) {
        console.error('Error checking session:', err);
        setError(t('auth.invalidResetLink'));
        setIsCheckingSession(false);
        setTimeout(() => {
          navigate('/forgot-password', { replace: true });
        }, 3000);
      }
    };

    checkSession();
  }, [navigate, t, location]);

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-400 to-blue-500 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <LoadingSpinner size="md" />
            <p className="mt-2 text-gray-600">{t('common.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!isValidSession && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-400 to-blue-500 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('common.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-400 to-blue-500 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">{t('auth.passwordUpdated')}</h2>
            <p className="mt-2 text-gray-600">
              {t('auth.passwordUpdatedMessage')}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-400 to-blue-500 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {t('auth.resetPassword')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.enterNewPassword')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.newPassword')} *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={values.password}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              />
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('auth.confirmPassword')} *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={values.confirmPassword}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div>
            <Button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="w-full"
            >
              {isSubmitting ? t('auth.resetting') : t('auth.resetPassword')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ResetPasswordForm;